import { useState } from "react";
import { api } from "../api";
import { ErrorNote } from "../components/Feedback";
import { Btn, ConfirmDialog, Field, Input, Textarea } from "../components/Shared";
import { useApi } from "../useApi";

// Edit (name, description) and Delete of a study or a dataset, for whoever edits it (its author,
// the project's curators, an admin; the server's can_edit) (task
// 2026-10-12 "API-only operations get screens"). `kind` is "study" | "dataset"; `path` its API
// path (/studies/2, /datasets/3). The delete dialog first loads {path}/deletion-impact, as the
// file dialog in dataset/FilesTab.jsx does: what deleting it does to a built or hosted database
// (services/source_deletes.py); the backend's refusal (409 while a build runs, ...) is shown in it.

export function EditNameForm({ kind, path, item, onSaved, onCancel }) {
  const [form, setForm] = useState({ name: item.name, description: item.description ?? "" });
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const label = kind === "study" ? "Study name" : "Dataset name";

  const submit = async e => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.patch(path, { name: form.name.trim(), description: form.description || null });
      onSaved();
    } catch (err) {
      setError(err);
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} aria-label={`Edit ${kind}`}
          style={{ display: "flex", flexDirection: "column", gap: 14, marginTop: 14 }}>
      <ErrorNote error={error} />
      <Field label={label} required>
        <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
      </Field>
      <Field label="Description">
        <Textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
      </Field>
      <div style={{ display: "flex", gap: 10 }}>
        <Btn type="submit" disabled={busy || !form.name.trim()}>Save</Btn>
        <Btn variant="secondary" onClick={onCancel}>Cancel</Btn>
      </div>
    </form>
  );
}

const WHAT_GOES = {
  study: "Its datasets, their files, column dictionaries and metadata are deleted with it.",
  dataset: "Its files, their column dictionaries and its metadata are deleted with it.",
};

export function DeleteSourceDialog({ kind, path, item, onDeleted, onClose }) {
  const impact = useApi(`${path}/deletion-impact`);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const title = `Delete ${kind}`;
  // ConfirmDialog compares the typed text trimmed: a name created with a trailing space stays deletable.

  const remove = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.del(path);
      onDeleted();
    } catch (err) {
      setError(err);
      setBusy(false);
    }
  };

  return (
    <ConfirmDialog title={title} confirmWord={item.name.trim()} confirmLabel={title} busy={busy}
                   onConfirm={remove} onClose={onClose}>
      <p style={{ margin: 0, fontSize: 14, color: "#374151" }}>
        Delete <strong>{item.name}</strong>? {WHAT_GOES[kind]} The files' bytes (every version) are kept
        until an admin purges them.
      </p>
      {impact.data && (
        <p data-testid="deletion-impact" style={{ margin: "10px 0 0", fontSize: 14, color: impact.data.versions.length ? "#92400e" : "#374151" }}>
          {impact.data.message}
        </p>
      )}
      <div style={{ marginTop: 12 }}><ErrorNote error={error || impact.error} /></div>
    </ConfirmDialog>
  );
}
